import {
  Users,
  FolderKanban,
  CheckCircle2,
  ArrowRight,
  Loader2,
} from "lucide-react";

import {
  useWorkspaceStats,
} from "../Dashboard/hooks/useWorkspaceStats";


const statusStyles = {
  Active:
    "bg-green-100 text-green-700",

  Planning:
    "bg-yellow-100 text-yellow-700",

  Completed:
    "bg-blue-100 text-blue-700",

  "On Hold":
    "bg-red-100 text-red-700",
};


const WorkspaceCard = ({
  workspace,
  onClick,
}) => {

  /**
   * IMPORTANT:
   *
   * Hook MUST be called before
   * any conditional return.
   */
  const stats =
    useWorkspaceStats(workspace);


  /**
   * Now we can conditionally render.
   */
  if (!workspace) {
    return null;
  }


  const workspaceName =
    workspace.workspace_name ||
    workspace.name ||
    "-";


  const description =
    workspace.description ||
    "No description available";


  const status =
    workspace.status ||
    "Planning";


  const owner =
    workspace.owner ||
    workspace.workspace_lead_name ||
    workspace.workspace_lead_employee_name ||
    "-";


  return (
    <div
      onClick={onClick}
      className="
        group
        cursor-pointer
        rounded-2xl
        border
        border-slate-200
        bg-white
        p-6
        shadow-sm
        transition-all
        duration-300
        hover:-translate-y-1
        hover:shadow-xl
      "
    >

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="flex items-start justify-between gap-4">

        <div className="min-w-0">

          <h2 className="
            truncate
            text-xl
            font-bold
            text-slate-800
          ">
            {workspaceName}
          </h2>


          <p className="
            mt-1
            line-clamp-2
            text-sm
            text-slate-500
          ">
            {description}
          </p>

        </div>


        <span
          className={`
            shrink-0
            rounded-full
            px-3
            py-1
            text-xs
            font-semibold
            ${
              statusStyles[status] ||
              "bg-slate-100 text-slate-600"
            }
          `}
        >
          {status}
        </span>

      </div>



      {/* =====================================================
          TASK PROGRESS
      ===================================================== */}

      <div className="mt-6">

        <div className="
          mb-2
          flex
          items-center
          justify-between
        ">

          <span className="
            text-sm
            font-medium
            text-slate-600
          ">
            Task Progress
          </span>


          {stats.loading ? (

            <Loader2
              size={16}
              className="
                animate-spin
                text-blue-600
              "
            />

          ) : (

            <span className="
              text-sm
              font-semibold
              text-blue-600
            ">
              {stats.progress}%
            </span>

          )}

        </div>


        {/* Progress Bar */}

        <div className="
          h-2
          overflow-hidden
          rounded-full
          bg-slate-200
        ">

          <div
            className="
              h-2
              rounded-full
              bg-blue-600
              transition-all
              duration-500
            "
            style={{
              width: `${stats.progress}%`,
            }}
          />

        </div>


        {/* Completed / Total */}

        <div className="
          mt-2
          flex
          justify-between
          text-xs
          text-slate-500
        ">

          <span>
            {stats.completedItems} completed
          </span>


          <span>
            {stats.items} total tasks
          </span>

        </div>

      </div>


      {/* =====================================================
          MEMBERS & TASKS
      ===================================================== */}

      <div className="
        mt-6
        grid
        grid-cols-2
        gap-3
      ">

        {/* MEMBERS */}

        <div className="
          flex
          items-center
          gap-3
          rounded-xl
          bg-slate-50
          p-3
        ">

          <div className="
            rounded-lg
            bg-blue-100
            p-2
          ">

            <Users
              size={18}
              className="text-blue-600"
            />

          </div>


          <div>

            <p className="
              text-xs
              text-slate-500
            ">
              Members
            </p>


            <p className="
              text-lg
              font-bold
              text-slate-700
            ">

              {stats.loading
                ? "..."
                : stats.members}

            </p>

          </div>

        </div>


        {/* TASKS */}

        <div className="
          flex
          items-center
          gap-3
          rounded-xl
          bg-slate-50
          p-3
        ">

          <div className="
            rounded-lg
            bg-purple-100
            p-2
          ">

            <FolderKanban
              size={18}
              className="text-purple-600"
            />

          </div>


          <div>

            <p className="
              text-xs
              text-slate-500
            ">
              Tasks
            </p>


            <p className="
              text-lg
              font-bold
              text-slate-700
            ">

              {stats.loading
                ? "..."
                : stats.items}

            </p>

          </div>

        </div>

      </div>


      {/* =====================================================
          FOOTER
      ===================================================== */}

      <div className="
        mt-6
        flex
        items-center
        justify-between
      ">

        <div className="
          flex
          items-center
          gap-2
          text-xs
          text-slate-500
        ">

          <CheckCircle2
            size={15}
            className="text-green-600"
          />

          {stats.completedItems} completed

        </div>


        <div className="
          flex
          items-center
          text-blue-600
          transition-transform
          duration-200
          group-hover:translate-x-1
        ">

          <span className="
            text-sm
            font-semibold
          ">
            View Workspace
          </span>


          <ArrowRight
            size={18}
            className="ml-2"
          />

        </div>

      </div>

    </div>
  );
};


export default WorkspaceCard;